import React, { useState, useCallback, useEffect } from "react";
import Search from "../../../molecules/search";
import CustomButton from "../../../atoms/CustomButton";
import { useCookies } from "react-cookie";
import { getData, patchData } from "../../../../services";
import VerticalMenu from "../../../icons/VerticalMenu";
import CustomMenu from "../../../atoms/CustomMenu";
import { v4 as uuidv4 } from "uuid";
import {
  getEmployeeList,
  getRolesList,
  getClientList,
  getBusinessSegmentList,
  getTeamsList,
  getAllProjects,
} from "../../../../services/basicGets";
import toast from "react-hot-toast";
import {
  errorStyles,
  confimationStyles,
  warningmoreStyles,
} from "../../../../assets/styles/toast";
import Pagination from "../../../molecules/Pagination";
import StatusBackground from "../../../atoms/StatusBackground";
import ColorDots from "../../../atoms/ColorDots";
import NoContentCard from "../../../atoms/NoContentCard";
import { Select, MenuItem, Tooltip } from "@mui/material";
import { ReportStatus, Priorities } from "../../../../helpers/staticData";
import { Loader, Upload } from "lucide-react";
import CommonFilter from "../../../atoms/CommonFilter";
import TaskCustomDataGrid from "../../../organisms/CustomDatagrid/EditableTable";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment/moment";
import DateRangePickerShortCuts from "../../../organisms/dateRangePicker";
import { endOfDay, startOfDay, subDays } from "rsuite/esm/internals/utils/date";
import { addISTOffset } from "../../../../helpers/formatTime";
import Email from "../../../../assets/images/email";
import { capitalizeFirst } from "../../../../helpers/cookieHelper";

const ReportApproval = ({ disabled = false }) => {
  //access details handling
  const accessData = useSelector((state) => state.accessDetails?.accessData);
  const projectAccess =
    accessData?.access?.find((module) => module.module === "Projects") || {};
  const isAdmin = accessData?.role?.label === "Admin";

  const canEdit = isAdmin || projectAccess?.is_edit || true;
  const canExport = isAdmin || projectAccess?.is_export;

  const [filters, setFilters] = useState({});
  const [cookies] = useCookies(["e_t", "euid", ""]);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchedValue, setSearchedValue] = useState();
  const [roles, setRoles] = useState([]);
  const [Teams, setTeams] = useState([]);
  const [Client, setClient] = useState([]);
  const [Employee, setEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isdownload, setIsDownload] = useState(false);
  const [Projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [acceptingInviteId, setAcceptingInviteId] = useState(null);
  const defaultToday = new Date();

  const [date, setDate] = useState({
    start_date: startOfDay(subDays(defaultToday, 14)), // 14 days ago at 00:00:00
    end_date: endOfDay(defaultToday), // today at 23:59:59.999
  });

  const navigate = useNavigate();

  const filterOptions = [
    {
      id: "status",
      label: "Status",
      // type: "multi-select",
      options: ReportStatus,
    },
    // ...(type === "Project"
    //   ? []
    //   : [
    {
      id: "project",
      label: "Project",
      type: "multi-select",
      options: Projects,
    },
    // ]),
  ];

  const getReportsData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getData({
        endpoint: "ProjectReportsGenerated/GetAllProjectReportsGenerated",
        params: {
          search_query: searchedValue,
          page: currentPage,
          limit: itemsPerPage,
          approvedBy:
            cookies?.eut === "Employee"
              ? JSON.stringify([cookies?.euid])
              : null,
          approved:
            filters?.status?.[0] === true
              ? true
              : filters?.status?.[0] === false
              ? false
              : null,
          projectIds: filters?.project
            ? JSON.stringify(filters?.project)
            : null,
          start_date: date?.start_date,
          end_date: date?.end_date,
        },

        token: cookies.e_t,
      });
      if (res) {
        setTableData(res?.data);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    } finally {
      setIsLoading(false);
    }
  }, [
    cookies.e_t,
    currentPage,
    itemsPerPage,
    searchedValue,
    filters,
    isAdmin,
    date,
  ]);

  useEffect(() => {
    if (accessData) {
      getReportsData();
    }
  }, [getReportsData, accessData]);

  useEffect(() => {
    (async () => {
      const resRoles = await getRolesList({
        token: cookies.e_t,
      });
      const empRoles = await getEmployeeList({
        params: { ...(!isAdmin && { employee_id: cookies.euid }) },
        token: cookies.e_t,
      });
      const clientRoles = await getClientList({
        token: cookies.e_t,
      });
      const projects = await getAllProjects({
        ...(!isAdmin && { params: { employee_id: cookies.euid } }),
        token: cookies.e_t,
      });
      setProjects(projects?.data);
      setClient(clientRoles);
      setEmployees(empRoles?.employeeResponseValueLabel);
      setEmployee(empRoles);
      setRoles(resRoles);
    })();
  }, []);

  const handleApprove = async (id, type) => {
    const dataToSend = {
      project_report_id: id,
      approved_by: cookies.euid,
      send_report_to_client: type === "sendmail" ? true : false,
    };
    setAcceptingInviteId(id);
    try {
      const res = await patchData({
        endpoint: "/ProjectReportsGenerated/ApproveProject",
        data: {},
        params: dataToSend,
        token: cookies?.e_t,
      });
      if (res?.status) {
        toast.success(res?.message || "Project Report Approved Successfully", {
          duration: 2000,
          style: confimationStyles,
        });
        getReportsData();
      } else {
        toast.success(res?.message, {
          duration: 2000,
          style: warningmoreStyles,
        });
      }
    } catch (error) {
      console.error("Approve project report error:", error);
      throw error;
    } finally {
      setAcceptingInviteId(null);
    }
  };

  const columns = [
    {
      field: "project_id",
      headerName: "Report Name",
      flex: 1,
      maxWidth: 230,
      renderCell: (params) => {
        const displayText = params.row.project?.name
          ? capitalizeFirst(params.row.project?.name)
          : "New Project";

        return (
          <Tooltip title={displayText} arrow>
            <span className="multiline-ellipsis">{displayText}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "generated_report_type",
      headerName: "Frequency",
      width: 120,
      renderCell: (params) => {
        const labels = {
          weekly: "Weekly",
          biweekly: "Bi-Weekly",
          fortnightly: "Fortnightly",
          monthly: "Monthly",
        };
        return <strong>{labels[params.value] || params.value}</strong>;
      },
    },

    {
      field: "approval_status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const Status = params.value === true ? "Approved" : "Pending";
        return <StatusBackground status={Status} />;
      },
    },
    {
      field: "created_at",
      headerName: "Generated On",
      width: 180,
      renderCell: (params) => (
        <div>{moment(params.value).format("DD MMM YYYY, hh:mm A")}</div>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 0.2,
      renderCell: (params) => {
        const ProjectReportID = params?.row?.id;
        const isAccepting = acceptingInviteId === ProjectReportID;
        const Status = params?.row?.approval_status;

        return (
          <div className="table-icons-container">
            <div className="d-flex gap-2" style={{ width: "100%" }}>
              {!Status && (
                <CustomButton
                  text="Approve"
                  type="btn-primary"
                  handleClick={() => handleApprove(ProjectReportID)}
                  iconRequired={false}
                />
              )}

              {/* {canExport && ( */}
              <>
                <CustomButton
                  text={
                    isAccepting
                      ? "Approving & Sending Mail..."
                      : "Approve & Send Mail"
                  }
                  handleClick={() => handleApprove(ProjectReportID, "sendmail")}
                  icon={
                    isAccepting ? (
                      <Loader className="animate-spin" size={18} />
                    ) : (
                      <Email />
                    )
                  }
                  disabled={isAccepting || disabled}
                  iconRequired
                  forMobile
                />
              </>
              {/* )} */}
            </div>
          </div>
        );
      },
    },
    //   ]
    // : []),
  ];

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((perPage) => {
    setCurrentPage(1);
    setItemsPerPage(perPage);
  }, []);

  const CustomNoRowsOverlay = React.memo(() => {
    return (
      <>
        <NoContentCard
          title="No Projects Found"
          type="Project"
          subtitle={
            "You haven’t started any projects yet. Create one to get going!"
          }
        />
      </>
    );
  });

  const handleFilterChange = (newFilters) => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    setFilters(newFilters);
  };

  return (
    <div id="Project-Management" className="main-container">
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2 segregation-container">
          <DateRangePickerShortCuts setDate={setDate} />
        </div>

        <div className="mt-2" style={{ width: "45%" }}>
          <CommonFilter
            filters={filterOptions}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>
      <div className="mt-3">
        <TaskCustomDataGrid
          columns={columns}
          CustomNoRowsOverlay={CustomNoRowsOverlay}
          rows={tableData?.data || []}
          onCellClick={(params, event) => {
            if (!params || typeof params.field !== "string") {
              return;
            }
            if (params.field === "project_id") {
              navigate(
                `/dashboard/project-reports/projectreportinfo/${params?.row?.project_id}`,
                {
                  state: {
                    name: params?.row?.project?.name,
                    start_date: addISTOffset(params?.row?.start_date_for_range),
                    end_date: addISTOffset(params?.row?.end_date_for_range),
                    approval_status: params?.row?.approval_status ,
                    project_report_id: params?.row?.id,
                  },
                }
              );
            }
          }}
          isLoading={isLoading}
          isLoadingStateProvided={true}
        />
      </div>

      {tableData?.total_records > 10 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={tableData?.total_records}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default ReportApproval;
